// Order Invite forecast + owner-facing wording (PR 1.3). Pure module, no IO —
// unit-tested from src/test/inviteForecast.test.ts.
//
// get_invite_forecast() (the RPC) is the source of truth; computeForecastFallback
// approximates its eligibility classification client-side so the "Today" hero
// and funnel still render while the RPC migration is not yet applied. The
// classification order MUST mirror sms-jobs/inviteRotationPlan.ts and the RPC —
// keep the three in sync when the eligibility rules change. Known limitation:
// the fallback only runs before v_customer_invite_status exists, so its members
// carry no invite history and a contactless customer who was already invited
// still counts as will_invite — the contactless invite-history cap applies
// only once the RPC lands.

import {
  addDaysToDateString,
  isRotationMaterialized,
  isRotationFailed,
  isInOrderWindow,
  localDate,
  localMidnightUTC,
  nextAllowedSendTime,
  orderWindowForRun,
  renderTemplate,
} from "@/utils/smsShared";
import { calculateSmsSegments } from "@/utils/smsSegments";
import type { CustomerMarketing } from "@/types/sms";

/** Store fields the invite template substitutes at send time. */
export interface InviteStoreVars {
  storeName: string;
  storePhone: string | null;
  storeAddress: string;
}

/** Renders the invite body exactly as the invite_rotation worker does
 *  (renderTemplate with the store fields), so previews and cost match the send. */
export function renderInviteBody(body: string, vars: InviteStoreVars): string {
  return renderTemplate(body, {
    location_name: vars.storeName,
    store_phone: vars.storePhone ?? "",
    store_address: vars.storeAddress,
  });
}

/** Segments one invite costs — computed from the real template, never
 *  hardcoded to 1 (an owner can add characters that push it to 2 or to UCS-2). */
export function inviteSegments(body: string, vars: InviteStoreVars): number {
  return calculateSmsSegments(renderInviteBody(body, vars)).segments;
}

const VALID_MOBILE = /^[2-9][0-9]{2}[2-9][0-9]{6}$/;

/** The eligibility funnel, one number per exclusion bucket; the buckets sum to
 *  `ordered_in_window` and `will_invite` is the count that will actually send. */
export interface InviteFunnel {
  ordered_in_window: number;
  already_subscribed: number;
  opted_out: number;
  invalid_or_disabled: number;
  over_max_invites: number;
  in_cooldown_no_new_order: number;
  will_invite: number;
}

export type FunnelBucket = Exclude<keyof InviteFunnel, "ordered_in_window">;

/** One customer in today's batch cohort, with the funnel bucket that decided
 *  whether they get the invite — the "who's in this batch" list. */
export interface InviteForecastMember {
  customer_phone: string;
  display_name: string | null;
  last_order_at: string;
  bucket: FunnelBucket | "will_invite";
}

/** The most members the forecast returns (RPC and fallback alike); the
 *  funnel counts always cover the whole cohort. */
export const FORECAST_MEMBER_LIMIT = 500;

export interface InviteForecast {
  /** `funnel.will_invite` for today's batch. */
  eligible_now: number;
  /** Today's batch cohort: everyone whose latest order fell on yesterday's
   *  store-local calendar date (orderWindowForRun at today's batch) — the
   *  invite materialiser's own window, so the number matches what the batch
   *  sends and stays fixed all day. */
  funnel: InviteFunnel;
  /** ISO timestamp of today's batch (the raw store-local send hour the
   *  rotation materialises at), or null when the store has no rotation. */
  today_batch_at: string | null;
  /** ISO timestamp of the next batch still to come (today's until the send
   *  hour passes, then tomorrow's), or null with no rotation. */
  next_batch_at: string | null;
  /** Once today's batch time has passed: how many tomorrow's batch would
   *  invite from today's orders so far. null while today's batch is ahead. */
  next_so_far: number | null;
  /** Today's cohort, newest order first, capped at FORECAST_MEMBER_LIMIT. */
  members: InviteForecastMember[];
  /** Weekday (0=Sunday) → typical sent count; `{}` until enough history. */
  typical_by_dow: Record<string, number>;
}

/** The funnel rows, in display order, each labelled in owner words. */
export const FUNNEL_STEPS: { key: FunnelBucket; label: string }[] = [
  { key: "already_subscribed", label: "already in your text club" },
  { key: "opted_out", label: "opted out or excluded from invites" },
  { key: "invalid_or_disabled", label: "no valid mobile number" },
  { key: "over_max_invites", label: "already got the most invites we send" },
  { key: "in_cooldown_no_new_order", label: "waiting out the follow-up cooldown" },
];

/** A member's bucket in owner words, for the "who's in this batch" list. */
export function bucketLabel(bucket: InviteForecastMember["bucket"]): string {
  if (bucket === "will_invite") return "Will be invited";
  const label = FUNNEL_STEPS.find((s) => s.key === bucket)?.label ?? bucket.replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Skip reasons (config-calendar §3.4), each in plain owner language for the
 *  batch-detail dialog: "why didn't she get the text?". */
export const SKIP_REASON_LABELS: Record<string, string> = {
  already_subscribed: "Already in the text club",
  opted_out: "Opted out",
  invalid_mobile: "No valid mobile number",
  in_cooldown: "Still in the follow-up cooldown",
  max_invites: "Already got the most invites we send",
  no_new_order: "No new order since the last invite",
  monthly_cap: "Monthly invite cap reached",
  daily_cap: "Daily send cap reached",
  kill_switch: "Sending was paused (emergency stop)",
  messaging_disabled: "Texting is turned off for this number",
  deleted: "Customer was removed",
  marketing_excluded: "You turned off marketing texts for them",
};

/** A skip reason in owner words, falling back to a readable form of an
 *  unrecognised code rather than showing the raw enum. */
export function skipReasonLabel(reason: string | null): string {
  if (!reason) return "Skipped";
  return SKIP_REASON_LABELS[reason] ?? reason.replace(/_/g, " ");
}

/** "(•••) •••-1234" from a bare-10 number, for the live feed — never the full
 *  number, since the feed is a glanceable log, not a contact list. */
export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const last4 = digits.slice(-4);
  return last4 ? `•••${last4}` : "a customer";
}

export type LiveFeedKind = "joined" | "opted_out" | "failed" | "batch";

export interface LiveFeedEvent {
  id: string;
  kind: LiveFeedKind;
  /** ISO timestamp used for ordering. */
  at: string;
  /** Owner-facing one-liner. */
  label: string;
}

/** A batch campaign row the feed summarises ("Sent 12 invites"). */
export interface FeedBatch {
  id: string;
  sent_count: number;
  created_at: string;
  effective_send_at: string | null;
}

/** A recipient row that produced a reply or a failure. */
export interface FeedRecipient {
  id: string;
  customer_phone: string;
  outcome: "subscribed" | "opted_out" | "no_reply" | null;
  status: string;
  replied_at: string | null;
  sent_at: string | null;
  created_at: string;
}

/** Merges batch sends and per-customer reply/failure events into one
 *  newest-first activity feed in owner words. Pure — the hook does the IO. */
export function buildLiveFeed(batches: FeedBatch[], recipients: FeedRecipient[]): LiveFeedEvent[] {
  const events: LiveFeedEvent[] = [];

  for (const b of batches) {
    if (b.sent_count > 0) {
      events.push({
        id: `batch:${b.id}`,
        kind: "batch",
        at: b.effective_send_at ?? b.created_at,
        label: `Sent ${b.sent_count} invite${b.sent_count === 1 ? "" : "s"}`,
      });
    }
  }

  for (const r of recipients) {
    const who = maskPhone(r.customer_phone);
    if (r.outcome === "subscribed") {
      events.push({ id: `join:${r.id}`, kind: "joined", at: r.replied_at ?? r.created_at, label: `${who} joined your text club` });
    } else if (r.outcome === "opted_out") {
      events.push({ id: `stop:${r.id}`, kind: "opted_out", at: r.replied_at ?? r.created_at, label: `${who} opted out` });
    } else if (r.status === "failed") {
      events.push({ id: `fail:${r.id}`, kind: "failed", at: r.sent_at ?? r.created_at, label: `Couldn’t reach ${who}` });
    }
  }

  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "Sunday" for 0, "Monday" for 1, … — for the typical-by-weekday hint. */
export function weekdayName(dow: number): string {
  return WEEKDAY_NAMES[dow] ?? "";
}

/** 0-23 → "11:00 AM", matching the Settings hour-select labels. */
export function formatHourLabel(hour: number): string {
  const h = hour % 24;
  const period = h < 12 ? "AM" : "PM";
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:00 ${period}`;
}

/** "today 11:00 AM" / "tomorrow 11:00 AM" / "Tue 11:00 AM" for the next batch,
 *  in the store's timezone. Pure so the status line is unit-testable. */
export function formatNextBatch(iso: string | null, timezone: string, now: Date = new Date()): string {
  if (!iso) return "not scheduled";
  const at = new Date(iso);
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  }).format(at);
  const atDay = localDate(at, timezone);
  const today = localDate(now, timezone);
  const tomorrow = addDaysToDateString(today, 1);
  if (atDay === today) return `today ${time}`;
  if (atDay === tomorrow) return `tomorrow ${time}`;
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: timezone }).format(at);
  return `${weekday} ${time}`;
}

export interface ForecastFallbackInput {
  /** v_customer_marketing rows — the fallback filters the order window itself. */
  members: (Pick<
    CustomerMarketing,
    | "customer_phone"
    | "opt_in_status"
    | "messaging_status"
    | "never_invite"
    | "invite_count"
    | "last_invited_at"
    | "last_order_at"
    | "deleted_at"
  > & {
    display_name?: string | null;
    history_invite_count?: number;
    history_last_invited_at?: string | null;
    /** The owner's marketing exclusion (not on the view; the RPC reads sms_contacts). */
    marketing_excluded?: boolean;
  })[];
  /** Store timezone — the calendar-day window is store-local. */
  timezone: string;
  followUps: number;
  cooldownDays: number;
  /** Today's batch time (computeTodayBatchAt) — the cohort's anchor. */
  todayBatchAt: string | null;
  /** The next batch still to come (computeNextBatchAt), for display. */
  nextBatchAt: string | null;
  /** The next raw send hour (computeNextRunAt) — next_so_far's anchor. */
  nextRunAt: string | null;
  now?: Date;
}

/** Classifies one candidate into its funnel bucket, mirroring the RPC and the
 *  materialiser's skip-reason precedence. Kill switch and caps are global
 *  states shown separately, so they are not part of this per-member split. */
function classify(
  m: ForecastFallbackInput["members"][number],
  followUps: number,
  cooldownDays: number,
  now: Date,
): FunnelBucket | "will_invite" {
  if (m.deleted_at) return "invalid_or_disabled";
  if (!VALID_MOBILE.test(m.customer_phone)) return "invalid_or_disabled";
  if (m.opt_in_status === "opted_in") return "already_subscribed";
  if (m.opt_in_status === "opted_out") return "opted_out";
  if (m.marketing_excluded) return "opted_out";
  if (m.never_invite) return "opted_out";
  if (m.messaging_status === "disabled") return "invalid_or_disabled";
  const inviteCount = Math.max(m.invite_count, m.history_invite_count ?? 0);
  const times = [m.last_invited_at, m.history_last_invited_at ?? null]
    .filter((t): t is string => !!t)
    .map((t) => new Date(t).getTime());
  const lastInvitedMs = times.length ? Math.max(...times) : null;
  if (inviteCount >= 1 + followUps) return "over_max_invites";
  if (inviteCount >= 1) {
    const cooledDown = lastInvitedMs !== null ? now.getTime() - lastInvitedMs >= cooldownDays * 86_400_000 : false;
    const orderedAfter =
      !!m.last_order_at && lastInvitedMs !== null && new Date(m.last_order_at).getTime() > lastInvitedMs;
    if (!cooledDown || !orderedAfter) return "in_cooldown_no_new_order";
  }
  return "will_invite";
}

/** Members whose latest order falls on the store-local calendar day before
 *  the batch at `runAtMs` — the materialiser's isWithinOrderWindow with
 *  orderWindowForRun(runAt). */
function inOrderWindow<M extends { last_order_at: string | null }>(members: M[], runAtMs: number, timezone: string): M[] {
  const window = orderWindowForRun(new Date(runAtMs), timezone);
  return members.filter((m) => isInOrderWindow(m.last_order_at, window));
}

/** Builds the forecast from a candidate list when the RPC is unavailable. */
export function computeForecastFallback(input: ForecastFallbackInput): InviteForecast {
  const now = input.now ?? new Date();
  // No rotation → no batch → an empty cohort, as the RPC returns (its rotation
  // CTE is empty, so nothing joins).
  const todayAnchor = input.todayBatchAt ? new Date(input.todayBatchAt).getTime() : null;
  const inWindow = todayAnchor === null ? [] : inOrderWindow(input.members, todayAnchor, input.timezone);

  const funnel: InviteFunnel = {
    ordered_in_window: inWindow.length,
    already_subscribed: 0,
    opted_out: 0,
    invalid_or_disabled: 0,
    over_max_invites: 0,
    in_cooldown_no_new_order: 0,
    will_invite: 0,
  };

  const members: InviteForecastMember[] = [];
  for (const m of inWindow) {
    const bucket = classify(m, input.followUps, input.cooldownDays, now);
    funnel[bucket] += 1;
    members.push({
      customer_phone: m.customer_phone,
      display_name: m.display_name ?? null,
      last_order_at: m.last_order_at as string,
      bucket,
    });
  }
  // Same order as the RPC's jsonb_agg: newest order first, then phone.
  members.sort(
    (a, b) =>
      new Date(b.last_order_at).getTime() - new Date(a.last_order_at).getTime() ||
      a.customer_phone.localeCompare(b.customer_phone),
  );

  // After today's batch time the next batch is tomorrow's: count who it would
  // invite from today's orders so far.
  let nextSoFar: number | null = null;
  if (todayAnchor !== null && input.nextRunAt) {
    const nextAnchor = new Date(input.nextRunAt).getTime();
    if (nextAnchor > todayAnchor) {
      nextSoFar = inOrderWindow(input.members, nextAnchor, input.timezone).filter(
        (m) => classify(m, input.followUps, input.cooldownDays, now) === "will_invite",
      ).length;
    }
  }

  return {
    eligible_now: funnel.will_invite,
    funnel,
    today_batch_at: input.todayBatchAt,
    next_batch_at: input.nextBatchAt,
    next_so_far: nextSoFar,
    members: members.slice(0, FORECAST_MEMBER_LIMIT),
    typical_by_dow: {},
  };
}

/** Today's occurrence of the raw store-local send hour — even once it has
 *  passed, and never nudged out of quiet hours, since that raw hour is when
 *  invite_rotation materialises the batch (the cron tick lands a few minutes
 *  after it, so orders in those minutes also join today's real batch). The
 *  fallback twin of the RPC's today_batch_at; it anchors the "Today" cohort. */
export function computeTodayBatchAt(sendTime: string, timezone: string, now: Date = new Date()): string {
  const sendHour = parseInt(sendTime.slice(0, 2), 10) || 0;
  const today = localDate(now, timezone);
  return new Date(localMidnightUTC(today, timezone).getTime() + sendHour * 3_600_000).toISOString();
}

/** The next raw store-local send hour still to come (today's until it passes,
 *  then tomorrow's) — when the next batch materialises, and so the anchor of
 *  next_so_far's window. The fallback twin of the RPC's next_run_at. */
export function computeNextRunAt(sendTime: string, timezone: string, now: Date = new Date()): string {
  const sendHour = parseInt(sendTime.slice(0, 2), 10) || 0;
  const today = localDate(now, timezone);
  let candidate = new Date(localMidnightUTC(today, timezone).getTime() + sendHour * 3_600_000);
  if (candidate.getTime() <= now.getTime()) {
    const tomorrow = addDaysToDateString(today, 1);
    candidate = new Date(localMidnightUTC(tomorrow, timezone).getTime() + sendHour * 3_600_000);
  }
  return candidate.toISOString();
}

export interface TodayBatchStateInput {
  /** Today's cohort will_invite count. */
  eligible: number;
  todayBatchAt: string | null;
  /** sms_invite_rotations.enabled. */
  enabled: boolean;
  /** The emergency stop (kill switch) is on. */
  paused: boolean;
  /** Today's `play='invite'` batch row. A backlog sweep can create it before
   *  the send hour, so it only counts as "ran" once todayBatchAt has passed
   *  AND the rotation has materialised it (isRotationMaterialized). */
  todayBatch: { sent_count: number; recipients_count: number; status: string; audience_filter?: unknown } | null;
  /** Today's invite texts queued and due (not_before ≤ now), not yet sent. */
  queuedCount: number;
  now?: Date;
}

export interface TodayBatchState {
  /** "upcoming": today's batch time is ahead; "ran": the batch time passed
   *  and a batch row exists for today; "missed": it passed with no row. */
  phase: "upcoming" | "ran" | "missed";
  count: number;
  /** Follows "invite(s)": "go out", "held", "went out", … */
  verb: string;
  note: string | null;
  /** Whether `count` texts actually send (drives "Cost today" vs "Would cost"). */
  sends: boolean;
}

/** What the "Today" hero says. "Held" only when invite texts are actually
 *  queued behind the emergency stop — never just because the stop is on. */
export function describeTodayBatch(input: TodayBatchStateInput): TodayBatchState {
  const now = input.now ?? new Date();
  const { eligible, enabled, paused, todayBatch, queuedCount } = input;

  const passed = !!input.todayBatchAt && new Date(input.todayBatchAt).getTime() <= now.getTime();

  // A backlog-only row (swept before the rotation ran) is not today's batch:
  // keep showing the forecast cohort until the rotation materialises it.
  const batchRan = !!todayBatch && isRotationMaterialized(todayBatch.audience_filter);

  if (todayBatch && batchRan && (passed || !input.todayBatchAt)) {
    if (isRotationFailed(todayBatch.status, todayBatch.audience_filter)) {
      return {
        phase: "ran",
        count: eligible,
        verb: "didn’t go out",
        note: "Today’s batch failed — see the batch history below.",
        sends: false,
      };
    }
    if (paused && queuedCount > 0) {
      return {
        phase: "ran",
        count: queuedCount,
        verb: "held",
        note: "Held by the emergency stop — they send once it’s turned off.",
        sends: true,
      };
    }
    if (!paused && queuedCount > 0) {
      return {
        phase: "ran",
        count: todayBatch.sent_count + queuedCount,
        verb: "sending",
        note: null,
        sends: true,
      };
    }
    return {
      phase: "ran",
      count: todayBatch.sent_count,
      verb: "went out",
      note: null,
      sends: true,
    };
  }

  const why =
    !enabled && paused
      ? "Order Invite is off and the emergency stop is on"
      : !enabled
        ? "Order Invite is off"
        : paused
          ? "the emergency stop is on"
          : null;

  if (passed) {
    return {
      phase: "missed",
      count: eligible,
      verb: why ? "missed" : "not sent yet",
      note: why ? `Today’s batch didn’t go out — ${why}.` : "Today’s batch hasn’t gone out yet.",
      sends: false,
    };
  }

  if (why) {
    return {
      phase: "upcoming",
      count: eligible,
      verb: "won’t go out",
      note: !enabled
        ? `${why.charAt(0).toUpperCase()}${why.slice(1)} — nothing sends until it’s turned on.`
        : "Paused by the emergency stop — nothing sends until it’s turned off.",
      sends: false,
    };
  }
  return { phase: "upcoming", count: eligible, verb: "go out", note: null, sends: true };
}

/** The next occurrence of `sendTime` (HH:MM:SS) in the store's timezone,
 *  nudged out of quiet hours — the fallback twin of the RPC's next_batch_at. */
export function computeNextBatchAt(
  sendTime: string,
  timezone: string,
  quietStart: number,
  quietEnd: number,
  now: Date = new Date(),
): string {
  const candidate = new Date(computeNextRunAt(sendTime, timezone, now));
  return nextAllowedSendTime(candidate, timezone, quietStart, quietEnd).toISOString();
}

/**
 * The invite batches that belong to a store-local calendar month ("2026-09").
 * A batch's day is its invite_occurrence_date (already store-local); a row
 * without one falls back to its created_at converted to the store's date, so
 * a batch created late on the last evening of the month in Los Angeles still
 * counts in that month even though UTC has rolled over.
 */
export function inviteBatchesInMonth<T extends { invite_occurrence_date: string | null; created_at: string }>(
  batches: T[],
  yyyyMm: string,
  timezone: string,
): T[] {
  return batches.filter(
    (b) => (b.invite_occurrence_date ?? localDate(new Date(b.created_at), timezone)).slice(0, 7) === yyyyMm,
  );
}
